// Adjuntos del chat de Atlas: qué archivo se acepta, cuánto pesa como máximo y
// cómo se traduce a los bloques de `content` que espera la API.
//
// Todo lo de acá es PURO (sin DOM, sin FileReader) para poder testearlo. La
// conversión de un `File` de verdad vive en `ui/prepararAdjunto.ts`, que es la
// única parte que necesita el browser.
//
// POR QUÉ VA INLINE Y NO A STORAGE: el chat de "Crear desde cero" sube sus
// referencias al bucket y le pasa al backend un path firmado, pero eso sirve
// porque ESE flujo después genera imágenes con esa URL. Acá el destinatario es
// el modelo, y el modelo no tiene ninguna herramienta para bajarse un link: si
// mandáramos la URL solo vería una cadena de texto. Por eso el contenido viaja
// en el turno, en base64, y por eso los límites de abajo son chicos.

import type { AdjuntoChat } from './tipos';

/** Cuántos archivos puede llevar un mismo mensaje. */
export const MAX_ADJUNTOS = 3;

/**
 * Tope del contenido de una imagen YA convertida (después de normalizar
 * formato y redimensionar).
 *
 * El número sale del backend: corta el body en 4 MB (`CHAT_BODY_LIMIT` en
 * `routes/agentChat.ts`) y el base64 infla un 33%. Con 700 KB × 3 adjuntos el
 * peor caso es ~2,8 MB de base64, que entra con aire para el historial y el
 * system prompt. Y ese peor caso se REPITE en cada turno siguiente, porque el
 * historial se manda entero: por eso el tope es bajo a propósito.
 */
export const MAX_BYTES_IMAGEN = 700_000;

/** Lado máximo de una imagen adjunta, en px. Suficiente para que el modelo lea
 *  un cartel o una etiqueta, sin mandar una foto de 12 MP. */
export const LADO_MAX_IMAGEN = 1200;

/** Tope de caracteres de un archivo de texto. Lo que pasa se recorta y se
 *  avisa: mejor un pedazo con aviso que un turno rechazado por tamaño. */
export const MAX_CHARS_TEXTO = 20_000;

/** Extensiones de texto que aceptamos. No entran binarios (docx, xlsx, pdf):
 *  no se pueden leer como texto y mandarlos crudos sería basura para el
 *  modelo. */
export const EXTENSIONES_TEXTO = [
  'txt',
  'md',
  'markdown',
  'csv',
  'tsv',
  'json',
  'log',
] as const;

/** Valor del `accept` del `<input type="file">`. */
export const ACEPTA_ADJUNTOS = `image/*,${EXTENSIONES_TEXTO.map((e) => `.${e}`).join(',')}`;

/** Los únicos `media_type` de imagen que acepta la API. Nuestra normalización
 *  siempre termina en JPEG o PNG; el resto cae a JPEG por las dudas. */
const MEDIA_TYPES_IMAGEN = ['image/jpeg', 'image/png'] as const;

export const MENSAJE_FORMATO_ADJUNTO =
  'Solo puedo leer imágenes (JPG, PNG, HEIC) y archivos de texto (txt, md, csv, json).';

export const mensajeLimiteAdjuntos = (): string =>
  `Podés mandar hasta ${MAX_ADJUNTOS} archivos por mensaje.`;

export const MENSAJE_IMAGEN_PESADA =
  'Esa imagen es muy pesada incluso después de achicarla. Probá con una más liviana.';

/** Extensión en minúsculas, sin el punto. '' si el nombre no tiene. */
export const extensionDe = (nombre: string): string => {
  const limpio = (nombre || '').toLowerCase().trim();
  const punto = limpio.lastIndexOf('.');
  return punto > 0 ? limpio.slice(punto + 1) : '';
};

/**
 * Qué clase de adjunto es un archivo, mirando MIME y extensión. `null` si no
 * lo aceptamos.
 *
 * Se mira la EXTENSIÓN además del MIME porque los `.md` y los `.csv` llegan
 * con `type` vacío en varios navegadores, y un `.json` a veces llega como
 * `application/json` (que no empieza con `text/`).
 */
export const claseDeArchivo = (
  nombre: string,
  mime: string,
): AdjuntoChat['clase'] | null => {
  const tipo = (mime || '').toLowerCase();
  const ext = extensionDe(nombre);
  if (tipo.startsWith('image/')) return 'imagen';
  if ((EXTENSIONES_TEXTO as readonly string[]).includes(ext)) return 'texto';
  if (tipo.startsWith('text/') || tipo === 'application/json') return 'texto';
  // HEIC/HEIF llegan con `type` vacío en algunos Android; los reconocemos por
  // extensión para que `normalizeImageForUpload` pueda convertirlos.
  if (ext === 'heic' || ext === 'heif') return 'imagen';
  return null;
};

/** Normaliza el `media_type` de una imagen a algo que la API acepte. */
export const mediaTypeImagen = (mime: string): string =>
  (MEDIA_TYPES_IMAGEN as readonly string[]).includes((mime || '').toLowerCase())
    ? mime.toLowerCase()
    : 'image/jpeg';

/** Cuántos adjuntos más entran. Nunca negativo. */
export const lugarLibreAdjuntos = (actuales: number): number =>
  Math.max(0, MAX_ADJUNTOS - actuales);

/** Peso legible para el chip. Sin decimales en KB, uno en MB. */
export const formatearPeso = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

/**
 * Los bloques de `content` de los adjuntos, en el formato de la API de
 * mensajes (mismo shape que usa el resto de los clientes del endpoint).
 *
 * Los archivos de texto van como bloque `text` con el nombre adelante: sin el
 * nombre, el modelo no tiene forma de referirse al archivo ("en el csv que
 * mandaste…") ni de distinguir dos adjuntos entre sí.
 */
export function bloquesDeAdjuntos(adjuntos?: AdjuntoChat[]): unknown[] {
  if (!adjuntos || adjuntos.length === 0) return [];
  const bloques: unknown[] = [];
  for (const a of adjuntos) {
    if (!a || !a.datos) continue;
    if (a.clase === 'imagen') {
      bloques.push({
        type: 'image',
        source: {
          type: 'base64',
          media_type: mediaTypeImagen(a.mediaType),
          data: a.datos,
        },
      });
    } else {
      bloques.push({
        type: 'text',
        text: `[Archivo adjunto: ${a.nombre}]\n${a.datos}`,
      });
    }
  }
  return bloques;
}

/**
 * El `content` de un mensaje del usuario.
 *
 * Sin adjuntos devuelve el string pelado (es lo que la API espera en el caso
 * normal y lo que ya mandaba este chat). Con adjuntos devuelve el array de
 * bloques, con los archivos PRIMERO y el texto último: es el orden que
 * recomienda Anthropic para que el modelo mire la imagen antes de leer la
 * consigna. Si no hay texto, se manda igual una línea corta — un turno de solo
 * imagen deja al modelo adivinando qué se espera de él.
 */
export function contenidoUsuario(
  texto: string,
  adjuntos?: AdjuntoChat[],
): string | unknown[] {
  const limpio = (texto || '').trim();
  const bloques = bloquesDeAdjuntos(adjuntos);
  if (bloques.length === 0) return limpio;
  return [
    ...bloques,
    { type: 'text', text: limpio || 'Mirá lo que te mando y decime qué ves.' },
  ];
}
